import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { Header } from "../components/Header";
import { Seo } from "../components/Seo";
import { useLang } from "../context/LanguageContext";
import { toast } from "sonner";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const { t } = useLang();
  const [params] = useSearchParams();
  const flow = params.get("flow") === "customer" ? "customer" : "restaurant";
  const backTarget = flow === "customer" ? "/login" : "/restaurant";
  const endpoint = flow === "customer" ? "/auth/forgot-password" : "/auth/restaurant/forgot-password";
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post(endpoint, { email });
      setSent(true);
    } catch {
      // Endpoint is silent by design — never reveal existence
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <Seo
        title="Mot de passe oublié — Vytali"
        description="Réinitialisez le mot de passe de votre compte Vytali."
        path="/forgot-password"
        noIndex
      />
      <Header />
      <div className="max-w-md mx-auto px-6 py-12">
        <button
          data-testid="back-to-login"
          onClick={() => navigate(backTarget)}
          className="inline-flex items-center gap-2 text-sm text-[#5C5C5C] hover:text-[#E63946] mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> {t("forgot.back")}
        </button>

        <div className="bg-white border-2 border-[#2B2B2B] rounded-2xl p-6 md:p-8 shadow-[8px_8px_0px_#E63946]">
          {sent ? (
            <div data-testid="forgot-sent" className="text-center py-4">
              <CheckCircle2 className="w-14 h-14 text-[#2A9D8F] mx-auto" />
              <h1 className="mt-4 text-2xl font-display font-black tracking-tight text-[#2B2B2B]">
                {t("forgot.sentTitle")}
              </h1>
              <p className="mt-3 text-sm text-[#5C5C5C] leading-relaxed">
                {t("forgot.sentDesc")}
              </p>
              <button
                data-testid="forgot-back-login-btn"
                onClick={() => navigate(backTarget)}
                className="mt-6 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#2B2B2B] text-white font-bold text-sm hover:scale-[1.02] transition-transform"
              >
                {t("forgot.backToLogin")}
              </button>
            </div>
          ) : (
            <>
              <h1 className="text-2xl md:text-3xl font-display font-black tracking-tight text-[#2B2B2B]">
                {t("forgot.title")}
              </h1>
              <p className="mt-2 text-sm text-[#5C5C5C]">
                {t("forgot.subtitle")}
              </p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C5C5C]" />
                  <input
                    data-testid="forgot-email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t("clogin.email")}
                    className="w-full h-12 pl-10 pr-4 rounded-xl bg-[#FDFBF7] border border-[#E5E0D8] outline-none focus:border-[#E63946] transition-colors"
                  />
                </div>
                <button
                  data-testid="forgot-submit"
                  type="submit"
                  disabled={busy}
                  className="w-full h-12 rounded-full bg-[#E63946] text-white font-bold hover:scale-[1.02] transition-transform disabled:opacity-50"
                >
                  {busy ? "…" : t("forgot.submit")}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
